import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react'
import axios from 'axios'
import { getApiUrl } from '../lib/utils'
import {
  setSessionToken,
  clearSessionToken,
} from '../lib/session'
import {
  captureReferralFromUrl,
  clearStoredReferralCode,
  getStoredReferralCode,
  normalizeReferralCode,
} from '../lib/referral'

const API = getApiUrl()

const AuthContext = createContext(null)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)

  const checkAuth = useCallback(async () => {
    // Skip /me check when returning from Google OAuth — AuthCallback handles session first.
    if (
      window.location.pathname === '/auth/callback' &&
      new URLSearchParams(window.location.search).has('code')
    ) {
      setLoading(false)
      return
    }

    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true,
      })
      setUser(response.data)
    } catch (error) {
      setUser(null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    checkAuth()
  }, [checkAuth])

  // A ?ref= code can land on any page (the landing page as often as /register),
  // so grab it as soon as the app mounts rather than only on the signup screen.
  useEffect(() => {
    captureReferralFromUrl()
  }, [])

  // The session token from the response body is kept as a fallback for
  // browsers that block the cross-site session cookie (see lib/session.ts).
  const applyAuthResponse = (data) => {
    const { session_token: sessionToken, ...userData } = data
    setSessionToken(sessionToken)
    setUser(userData)
    return userData
  }

  const login = async (email, password) => {
    const response = await axios.post(
      `${API}/auth/login`,
      { email, password },
      {
        withCredentials: true,
      },
    )
    return applyAuthResponse(response.data)
  }

  const register = async (email, password, name, referralCode) => {
    // An explicitly typed code wins over one remembered from a ?ref= link.
    const code = normalizeReferralCode(referralCode) || getStoredReferralCode()
    const response = await axios.post(
      `${API}/auth/register`,
      { email, password, name, ...(code ? { referral_code: code } : {}) },
      {
        withCredentials: true,
      },
    )
    // Attributed (or rejected server-side) either way — don't let it attach
    // itself to the next account created in this tab.
    clearStoredReferralCode()
    return applyAuthResponse(response.data)
  }

  const loginWithGoogle = () => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID
    if (!clientId) {
      console.error('REACT_APP_GOOGLE_CLIENT_ID is not configured')
      return
    }
    const redirectUri = `${window.location.origin}/auth/callback`
    const state = Math.random().toString(36).slice(2)
    sessionStorage.setItem('oauth_state', state)
    const params = new URLSearchParams({
      client_id: clientId,
      redirect_uri: redirectUri,
      response_type: 'code',
      scope: 'openid email profile',
      access_type: 'offline',
      state,
    })
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params}`
  }

  const handleGoogleCallback = async (code: string) => {
    // Referral survives the OAuth round-trip in sessionStorage; the server only
    // applies it when this callback actually creates a new account.
    const referralCode = getStoredReferralCode()
    const response = await axios.post(
      `${API}/auth/google/session`,
      {
        code,
        redirect_uri: `${window.location.origin}/auth/callback`,
        ...(referralCode ? { referral_code: referralCode } : {}),
      },
      { withCredentials: true },
    )
    clearStoredReferralCode()
    return applyAuthResponse(response.data)
  }

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true })
    } catch (error) {
      console.error('Logout error:', error)
    }
    clearSessionToken()
    setUser(null)
  }

  const value = {
    user,
    loading,
    login,
    register,
    loginWithGoogle,
    handleGoogleCallback,
    logout,
    setUser,
    checkAuth,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
